import React, { useEffect, useState } from "react";
import { useOutletContext, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { Button, Card, Pill, Spinner } from "@/components/ui-kit";
import QuestionCard from "@/components/QuestionCard";
import MathText from "@/components/MathText";
import NumberLine, { isCountingSkill } from "@/components/NumberLine";
import { ArrowRight, Trophy, RotateCcw, MessagesSquare, LifeBuoy, PartyPopper, TrendingUp, TrendingDown, Minus, Sparkles } from "lucide-react";

const DIFF = {
  1: { label: "Easier", icon: TrendingDown, tone: "#2A8555" },
  2: { label: "Medium", icon: Minus, tone: "#C88A2B" },
  3: { label: "Harder", icon: TrendingUp, tone: "#D96B43" },
};

export default function Practice({ mode }) {
  const { studentId, reload, celebrateBadges } = useOutletContext();
  const { skillId } = useParams();
  const nav = useNavigate();
  const isMastery = mode === "mastery";

  const [meta, setMeta] = useState(null);
  const [item, setItem] = useState(null);
  const [answer, setAnswer] = useState("");
  const [feedback, setFeedback] = useState(null);
  const [answered, setAnswered] = useState(0);
  const [targetLen, setTargetLen] = useState(3);
  const [isProbe, setIsProbe] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [start] = useState(Date.now());

  useEffect(() => {
    api.post(`/students/${studentId}/quiz/start`, { skill_id: skillId, mode }).then(({ data }) => {
      setMeta(data); setItem(data.item); setTargetLen(data.target_len);
    }).catch(() => {});
  }, [studentId, skillId, mode]);

  if (!meta) return <Spinner label={isMastery ? "Preparing mastery check" : "Loading practice"} />;

  const check = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/students/${studentId}/quiz/${meta.session_id}/answer`, {
        item_id: item.item_id, answer, duration_sec: Math.round((Date.now() - start) / 1000),
      });
      setFeedback({ correct: data.correct, explanation: data.explanation, answer: data.answer });
      setAnswered((n) => n + 1);
      if (data.done) {
        setResult(data);
        if (isMastery && data.passed) { toast.success("Skill mastered! 🎉"); reload(); }
        if (isMastery && data.parent_alert) toast.warning("Your parent was notified for extra support here.");
        if (data.new_badges && data.new_badges.length) celebrateBadges(data.new_badges);
      } else {
        setResult({ pendingNext: data.item, target_len: data.target_len, is_probe: data.is_probe });
      }
    } finally { setBusy(false); }
  };

  const next = () => {
    setItem(result.pendingNext);
    setTargetLen(result.target_len || targetLen);
    setIsProbe(!!result.is_probe);
    setFeedback(null);
    setAnswer("");
    setResult(null);
  };

  const remediate = async () => {
    try {
      const { data } = await api.post(`/students/${studentId}/remediation`, { skill_id: skillId });
      const msg = data.added.length ? `Added foundational skill: ${data.added.join(", ")}` : "Added a confidence-building review.";
      toast.success(msg + (data.confidence_win ? ` Plus a quick win: ${data.confidence_win.name}.` : ""));
      reload();
      nav(`/student/${studentId}/path`);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  if (result && result.done) return (
    <FinalScreen result={result} isMastery={isMastery} skillId={skillId} studentId={studentId} nav={nav}
      onRetry={() => window.location.reload()} onRemediate={remediate} />
  );

  const d = DIFF[item.difficulty] || DIFF[2];
  const progress = Math.min(100, Math.round((answered / targetLen) * 100));

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-[var(--ns-subtle)]">{isMastery ? "Adaptive mastery check" : "Adaptive practice"}</div>
          <h1 className="font-head text-xl font-bold">{meta.name}</h1>
        </div>
        <div className="flex flex-col items-end gap-1">
          <Pill tone="muted" className="flex items-center gap-1" data-testid="difficulty-pill">
            <d.icon size={13} style={{ color: d.tone }} /> {d.label}
          </Pill>
          {isProbe && <Pill tone="gold" data-testid="probe-pill"><Sparkles size={12} /> Quick check</Pill>}
        </div>
      </div>
      <div className="h-2 rounded-full bg-[var(--ns-surface-subtle)] overflow-hidden mb-1">
        <div className="h-full bg-[var(--ns-primary)] transition-all duration-500" style={{ width: `${progress}%` }} />
      </div>
      <div className="text-xs text-[var(--ns-subtle)] mb-5">Question {answered + 1} · difficulty adapts to your answers</div>

      <motion.div key={item.item_id} initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.25 }}>
        <Card className="p-6">
          <QuestionCard item={item} value={answer} onChange={setAnswer} disabled={!!feedback} result={feedback ? { ...feedback } : null} />
        </Card>
      </motion.div>

      {isCountingSkill(skillId) && (
        <div className="mt-4" data-testid="practice-number-line">
          <div className="text-xs font-semibold uppercase tracking-wider text-[var(--ns-subtle)] mb-2">Count it out</div>
          <NumberLine max={20} />
        </div>
      )}

      <div className="mt-5">
        {!feedback ? (
          <Button className="w-full" data-testid="check-answer-btn" disabled={busy || (answer === "")} onClick={check}>
            {busy ? "Checking…" : "Check answer"}
          </Button>
        ) : result && result.pendingNext ? (
          <Button className="w-full" data-testid="next-question-btn" onClick={next}>Next question <ArrowRight size={16} /></Button>
        ) : (
          <div className="text-center text-sm text-[var(--ns-muted)]">Finishing up…</div>
        )}
      </div>
      <Button variant="ghost" className="mt-3 w-full text-sm" onClick={() => nav(`/student/${studentId}/tutor/${skillId}`)}><MessagesSquare size={15} /> Stuck? Ask the tutor</Button>
    </div>
  );
}

function FinalScreen({ result, isMastery, skillId, studentId, nav, onRetry, onRemediate }) {
  const total = result.total || 1;
  const correct = result.correct_count ?? 0;
  const pct = Math.round((result.score ?? correct / total) * 100);
  const passed = result.passed;
  const [booster, setBooster] = useState(null);

  useEffect(() => {
    if (isMastery && !passed) {
      api.get(`/students/${studentId}/confidence-booster?after=${skillId}`).then(({ data }) => data.available && setBooster(data));
    }
  }, [isMastery, passed, studentId, skillId]);
  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} data-testid="results-screen">
      <Card className="p-7 text-center mb-5">
        {isMastery ? (passed ? <PartyPopper size={44} className="mx-auto text-[var(--ns-gold)]" /> : <RotateCcw size={40} className="mx-auto text-[var(--ns-muted)]" />)
          : <Trophy size={40} className="mx-auto text-[var(--ns-gold)]" />}
        <h1 className="font-head text-2xl font-bold mt-3">{isMastery ? (passed ? "Mastered!" : "Almost there") : "Practice complete"}</h1>
        <div className="font-head text-4xl font-bold text-[var(--ns-primary)] mt-2">{correct}/{total}</div>
        <p className="text-[var(--ns-muted)] mt-1">{pct}% (difficulty-weighted)</p>
        {(result.new_badges || []).length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2 justify-center">
            {result.new_badges.map((b) => <Pill key={b.key} tone="gold" data-testid="earned-badge"><Sparkles size={12} /> {b.title}</Pill>)}
          </div>
        )}
      </Card>

      {result.misconception_review?.length > 0 && (
        <Card className="p-5 mb-4">
          <div className="text-xs font-semibold uppercase tracking-wider text-[var(--ns-subtle)] mb-2">Review what tripped you up</div>
          <div className="grid gap-3">
            {result.misconception_review.map((m, i) => (
              <div key={i} className="rounded-[var(--ns-radius)] bg-[#FDECE8] p-3 text-sm text-[#8A2C1D]">
                <MathText text={m.prompt || ""} />
                <div className="mt-1 text-[#B23A28]"><MathText text={m.explanation} /></div>
              </div>
            ))}
          </div>
        </Card>
      )}

      <div className="grid gap-3">
        {isMastery && !passed && booster && (
          <Card className="p-4 border-l-4" style={{ borderLeftColor: "#2A8555" }} data-testid="confidence-booster">
            <div className="flex items-center gap-2 mb-1"><Sparkles size={16} className="text-[#2A8555]" /><span className="text-sm font-semibold">Finish on a high note</span></div>
            <p className="text-sm text-[var(--ns-muted)]">{booster.reason}</p>
            <Button variant="subtle" className="mt-3" data-testid="booster-btn"
              onClick={() => nav(`/student/${studentId}/practice/${booster.skill_id}`)}>
              Quick win: {booster.name} <ArrowRight size={15} />
            </Button>
          </Card>
        )}
        {isMastery && !passed && (
          <Button variant="accent" data-testid="remediation-btn" onClick={onRemediate}><LifeBuoy size={16} /> Insert a foundational review into my plan</Button>
        )}
        <div className="grid grid-cols-2 gap-3">
          <Button variant="outline" data-testid="retry-btn" onClick={onRetry}><RotateCcw size={16} /> Try again</Button>
          <Button variant="ghost" onClick={() => nav(`/student/${studentId}/tutor/${skillId}`)}><MessagesSquare size={16} /> Ask tutor</Button>
        </div>
        <Button data-testid="back-today-btn" onClick={() => nav(`/student/${studentId}/today`)}>Back to today</Button>
      </div>
    </motion.div>
  );
}
